import { Suspense, lazy } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { AdminRoute } from "@/components/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HelmetProvider } from "react-helmet-async";
import NotFound from "@/pages/not-found";
import Home from "@/pages/Home";
import BuildConfigurator from "@/pages/BuildConfigurator";
import ClientPortal from "@/pages/ClientPortal";
import Billing from "@/pages/Billing";
import SiteCloner from "@/pages/SiteCloner";
import PlatformServices from "@/pages/PlatformServices";
import PlatformOrderStatus from "@/pages/PlatformOrderStatus";

// Lazy load all admin routes for code splitting
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AdminHome = lazy(() => import("@/pages/admin/Home"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/Orders"));
const AdminLeads = lazy(() => import("@/pages/admin/Leads"));
const AdminAnalytics = lazy(() => import("@/pages/admin/Analytics"));
const AdminAuditLog = lazy(() => import("@/pages/admin/AuditLog"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminJobs = lazy(() => import("@/pages/admin/Jobs"));
const AdminJobDetail = lazy(() => import("@/pages/admin/JobDetail"));
const AdminFactory = lazy(() => import("@/pages/admin/Factory"));
const AdminDeployments = lazy(() => import("@/pages/admin/Deployments"));
const AdminEvidenceVault = lazy(() => import("@/pages/admin/EvidenceVaultPage"));
const AdminBuilder = lazy(() => import("@/pages/admin/Builder"));
const AdminCustomerDeliveries = lazy(() => import("@/pages/admin/CustomerDeliveries"));
const AdminKnowledgeCenter = lazy(() => import("@/pages/admin/KnowledgeCenter"));
const AdminHelm = lazy(() => import("@/pages/admin/Helm"));
const AdminVisualAutomationStudio = lazy(() => import("@/pages/admin/VisualAutomationStudio"));
const AdminOmnichannelInbox = lazy(() => import("@/pages/admin/OmnichannelInbox"));
const AdminProductionOps = lazy(() => import("@/pages/admin/ProductionOps"));

// AR Dynamics OS — Platform admin pages (Phase 8)
const PlatformOverview = lazy(() => import("@/pages/admin/PlatformOverview"));
const PlatformOrchestrate = lazy(() => import("@/pages/admin/PlatformOrchestrate"));
const PlatformTenants = lazy(() => import("@/pages/admin/PlatformTenants"));
const PlatformTenantDetail = lazy(() => import("@/pages/admin/PlatformTenantDetail"));
const PlatformAgencies = lazy(() => import("@/pages/admin/PlatformAgencies"));
const PlatformAgencyDetail = lazy(() => import("@/pages/admin/PlatformAgencyDetail"));
const PlatformJobs = lazy(() => import("@/pages/admin/PlatformJobs"));
const PlatformWebhooks = lazy(() => import("@/pages/admin/PlatformWebhooks"));

// Loading fallback for admin routes
const AdminLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      <span className="text-muted-foreground">Loading...</span>
    </div>
  </div>
);

function Router() {
  return (
    <Switch>
      {/* Public Routes - Eager loaded */}
      <Route path="/" component={Home} />
      <Route path="/build" component={BuildConfigurator} />
      <Route path="/quote" component={BuildConfigurator} />
      <Route path="/billing" component={Billing} />
      <Route path="/portal" component={ClientPortal} />
      <Route path="/client" component={ClientPortal} />
      <Route path="/site-cloner" component={SiteCloner} />
      <Route path="/platform-services" component={PlatformServices} />
      <Route path="/platform/order/:id" component={PlatformOrderStatus} />
      
      {/* Admin Routes - Lazy loaded with Suspense */}
      <Route path="/admin/login">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminLogin />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/home">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminHome} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminHome} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/dashboard">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminDashboard} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/orders">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminOrders} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/leads">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminLeads} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/factory">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminFactory} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/jobs">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminJobs} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/jobs/:id">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminJobDetail} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/deployments">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminDeployments} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/evidence-vault">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminEvidenceVault} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/builder">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminBuilder} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/customer-deliveries">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminCustomerDeliveries} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/knowledge">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminKnowledgeCenter} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/helm">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminHelm} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/visual-automation">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminVisualAutomationStudio} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/omnichannel">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminOmnichannelInbox} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/production-ops">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminProductionOps} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/analytics">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminAnalytics} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/audit-log">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminAuditLog} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/admin-users">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminUsers} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/settings">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={AdminSettings} />
          </Suspense>
        )}
      </Route>

      {/* AR Dynamics OS — Platform routes */}
      <Route path="/admin/platform/orchestrate">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformOrchestrate} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformOverview} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/tenants">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformTenants} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/tenants/:id">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformTenantDetail} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/agencies">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformAgencies} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/agencies/:id">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformAgencyDetail} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/jobs">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformJobs} />
          </Suspense>
        )}
      </Route>
      <Route path="/admin/platform/webhooks">
        {() => (
          <Suspense fallback={<AdminLoadingFallback />}>
            <AdminRoute component={PlatformWebhooks} />
          </Suspense>
        )}
      </Route>

      {/* 404 Route */}
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Router />
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
